import { useState, useCallback, useEffect } from 'react';
import type { Patient } from '../services/patientService';

export interface PatientTab {
  patientId: string;
  firstName: string;
  lastName: string;
  initials: string;
  insurance?: string;
  gender?: string;
  birthDate?: string;
  allergies?: string;
}

const SESSION_KEY = 'doctif_open_patient_tabs';
const ACTIVE_TAB_KEY = 'doctif_active_patient_tab';

function buildTab(patient: Patient): PatientTab {
  return {
    patientId: patient.id,
    firstName: patient.firstName,
    lastName: patient.lastName,
    initials: `${patient.firstName?.charAt(0) || ''}${patient.lastName?.charAt(0) || ''}`.toUpperCase(),
    insurance: patient.insurance || '',
    gender: patient.gender || '',
    birthDate: patient.birthDate || patient.dateOfBirth || '',
    allergies: patient.medical_history_allergies || '',
  };
}

function loadFromSession(): { tabs: PatientTab[]; activeId: string | null } {
  try {
    const tabsJson = sessionStorage.getItem(SESSION_KEY);
    const activeId = sessionStorage.getItem(ACTIVE_TAB_KEY);
    const tabs = tabsJson ? JSON.parse(tabsJson) : [];
    return { tabs, activeId };
  } catch {
    return { tabs: [], activeId: null };
  }
}

function saveToSession(tabs: PatientTab[], activeId: string | null) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(tabs));
    if (activeId) {
      sessionStorage.setItem(ACTIVE_TAB_KEY, activeId);
    } else {
      sessionStorage.removeItem(ACTIVE_TAB_KEY);
    }
  } catch {
    // sessionStorage might be full or unavailable
  }
}

export function usePatientTabs() {
  const [tabs, setTabs] = useState<PatientTab[]>(() => loadFromSession().tabs);
  const [activeTabId, setActiveTabId] = useState<string | null>(() => loadFromSession().activeId);

  // Persist to sessionStorage on change
  useEffect(() => {
    saveToSession(tabs, activeTabId);
  }, [tabs, activeTabId]);

  const openTab = useCallback((patient: Patient) => {
    setTabs(prev => {
      const exists = prev.find(t => t.patientId === patient.id);
      if (exists) {
        // Already open — just switch to it
        setActiveTabId(patient.id);
        return prev;
      }
      // Add new tab (max 8 — evict oldest when full)
      let newTabs: PatientTab[];
      if (prev.length >= 8) {
        // Evict the oldest (first) tab
        console.warn(`[PatientTabs] Maximum 8 onglets atteint — fermeture de ${prev[0].firstName} ${prev[0].lastName}`);
        newTabs = [...prev.slice(1), buildTab(patient)];
      } else {
        newTabs = [...prev, buildTab(patient)];
      }
      setActiveTabId(patient.id);
      return newTabs;
    });
  }, []);

  const closeTab = useCallback((patientId: string) => {
    setTabs(prev => {
      const idx = prev.findIndex(t => t.patientId === patientId);
      if (idx === -1) return prev;

      const newTabs = prev.filter(t => t.patientId !== patientId);

      // If we're closing the active tab, switch to adjacent
      setActiveTabId(current => {
        if (current !== patientId) return current;
        if (newTabs.length === 0) return null;
        // Prefer the tab at the same index, or the last one
        const nextIdx = Math.min(idx, newTabs.length - 1);
        return newTabs[nextIdx].patientId;
      });

      return newTabs;
    });
  }, []);

  const switchTab = useCallback((patientId: string) => {
    setActiveTabId(patientId);
  }, []);

  const activeTab = tabs.find(t => t.patientId === activeTabId) || null;

  return {
    tabs,
    activeTabId,
    activeTab,
    openTab,
    closeTab,
    switchTab,
  };
}
